// src/components/mensagens/InlineMessageEditor.jsx
// ============================================================================
// 🎯 COMPONENTE: InlineMessageEditor — V1 ENTERPRISE
// 📋 Função: Editor inline que substitui a bolha de mensagem durante edição
// 📱 Responsivo: Altura adaptável · Teclado nativo mobile · Toolbar flutuante
// 🎨 Design: Minimalista · Animações GPU-accelerated · Feedback visual instantâneo
// 🔒 Robusto: Validação em tempo real · Auto-resize · Anti-XSS · Undo (Ctrl+Z)
// ============================================================================
// ⌨️ Atalhos:
//    Ctrl+Enter  → Salvar
//    Escape      → Cancelar
//    Ctrl+Z      → Desfazer (histórico local)
// ============================================================================

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  X,
  RotateCcw,
  AlertCircle,
  Loader2,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { sanitizeMessageForEdit, truncateText } from './helpers';

// ============================================================================
// 🎨 CONSTANTES
// ============================================================================
const CONSTANTS = {
  MAX_LENGTH: 2000,
  MIN_LENGTH: 1,
  MAX_LENGTH_WARNING: 1800, // Aviso amarelo ao atingir
  DEBOUNCE_MS: 150,
  AUTO_SAVE_MS: 30000, // Auto-save a cada 30s (backup no localStorage)
  MAX_UNDO_STACK: 20,
};

// ============================================================================
// 🎯 HOOK: useAutoResize
// ============================================================================
function useAutoResize(textareaRef, value, maxHeight = 400) {
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    // Reset altura para calcular scrollHeight real
    el.style.height = 'auto';
    const scrollHeight = el.scrollHeight;
    const newHeight = Math.min(scrollHeight, maxHeight);
    el.style.height = `${newHeight}px`;

    // Scroll interno se exceder maxHeight
    el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, maxHeight, textareaRef]);
}

// ============================================================================
// 🎯 HOOK: useUndoStack
// ============================================================================
function useUndoStack(initialValue = '') {
  const [stack, setStack] = useState([initialValue]);
  const [pointer, setPointer] = useState(0);

  const push = useCallback(
    (value) => {
      setStack((prev) => {
        const newStack = prev.slice(0, pointer + 1);
        newStack.push(value);
        if (newStack.length > CONSTANTS.MAX_UNDO_STACK) {
          newStack.shift();
        }
        return newStack;
      });
      setPointer((prev) => Math.min(prev + 1, CONSTANTS.MAX_UNDO_STACK - 1));
    },
    [pointer]
  );

  const undo = useCallback(() => {
    if (pointer > 0) {
      const newPointer = pointer - 1;
      setPointer(newPointer);
      return stack[newPointer];
    }
    return null;
  }, [pointer, stack]);

  const redo = useCallback(() => {
    if (pointer < stack.length - 1) {
      const newPointer = pointer + 1;
      setPointer(newPointer);
      return stack[newPointer];
    }
    return null;
  }, [pointer, stack]);

  const reset = useCallback(
    (value) => {
      setStack([value]);
      setPointer(0);
    },
    []
  );

  return {
    current: stack[pointer] || initialValue,
    push,
    undo,
    redo,
    reset,
    canUndo: pointer > 0,
    canRedo: pointer < stack.length - 1,
  };
}

// ============================================================================
// 🎯 HOOK: useAutoSave
// ============================================================================
function useAutoSave(messageId, value, enabled = true) {
  const saveRef = useRef(value);
  saveRef.current = value;

  useEffect(() => {
    if (!enabled || !messageId) return;

    const interval = setInterval(() => {
      try {
        const key = `inline_editor_backup_${messageId}`;
        if (saveRef.current) {
          localStorage.setItem(
            key,
            JSON.stringify({
              value: saveRef.current,
              timestamp: Date.now(),
            })
          );
        }
      } catch {
        // localStorage pode estar cheio — ignorar silenciosamente
      }
    }, CONSTANTS.AUTO_SAVE_MS);

    return () => {
      clearInterval(interval);
      // Limpar backup ao desmontar se salvou com sucesso
    };
  }, [messageId, enabled]);

  // Recuperar backup
  useEffect(() => {
    if (!messageId || !enabled) return;

    try {
      const key = `inline_editor_backup_${messageId}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const backup = JSON.parse(raw);
        // Backup válido por 1 hora
        if (Date.now() - backup.timestamp < 3600000) {
          return backup.value;
        }
      }
    } catch {
      // Ignorar erros de parse
    }
    return null;
  }, [messageId, enabled]);
}

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================
function InlineMessageEditor({
  message = null,
  onSave,
  onCancel,
  loading = false,
  primaryColor = '#10b981',
}) {
  const isMobile = useIsMobile();
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const prevMessageRef = useRef(null);

  // ==========================================================================
  // 📊 ESTADOS
  // ==========================================================================
  const initialValue = sanitizeMessageForEdit(message?.message || '');
  const [value, setValue] = useState(initialValue);
  const [isExpanded, setIsExpanded] = useState(false);
  const [focused, setFocused] = useState(false);
  const [validation, setValidation] = useState({ valid: true, message: '' });

  const undo = useUndoStack(initialValue);
  const backupValue = useAutoSave(message?.id, value, focused);

  // ==========================================================================
  // 🔄 SINCRONIZAR QUANDO MENSAGEM MUDAR
  // ==========================================================================
  useEffect(() => {
    if (message?.id !== prevMessageRef.current?.id) {
      const sanitized = sanitizeMessageForEdit(message?.message || '');
      setValue(sanitized);
      undo.reset(sanitized);
      prevMessageRef.current = message;
    }
  }, [message, undo]);

  // ==========================================================================
  // 🎯 AUTO-FOCO COM DELAY PARA ANIMAÇÃO
  // ==========================================================================
  useEffect(() => {
    const timer = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Colocar cursor no final
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // ==========================================================================
  // 📐 AUTO-RESIZE
  // ==========================================================================
  useAutoResize(
    textareaRef,
    value,
    isExpanded ? 600 : isMobile ? 200 : 300
  );

  // ==========================================================================
  // ✅ VALIDAÇÃO EM TEMPO REAL
  // ==========================================================================
  useEffect(() => {
    if (!value || value.trim().length < CONSTANTS.MIN_LENGTH) {
      setValidation({
        valid: false,
        message: 'A mensagem não pode estar vazia',
      });
    } else if (value.length > CONSTANTS.MAX_LENGTH) {
      setValidation({
        valid: false,
        message: `Máximo de ${CONSTANTS.MAX_LENGTH} caracteres (${value.length}/${CONSTANTS.MAX_LENGTH})`,
      });
    } else if (value === sanitizeMessageForEdit(message?.message || '')) {
      setValidation({ valid: true, message: 'Nenhuma alteração' });
    } else {
      setValidation({ valid: true, message: '' });
    }
  }, [value, message]);

  // ==========================================================================
  // 🎮 HANDLERS
  // ==========================================================================
  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setValue(newValue);
      // Push para undo stack com debounce
      const timer = setTimeout(() => undo.push(newValue), CONSTANTS.DEBOUNCE_MS);
      return () => clearTimeout(timer);
    },
    [undo]
  );

  const handleSave = useCallback(() => {
    if (!validation.valid || loading) return;

    const sanitized = sanitizeMessageForEdit(value);
    if (!sanitized) return;

    // Limpar backup
    if (message?.id) {
      try {
        localStorage.removeItem(`inline_editor_backup_${message.id}`);
      } catch {}
    }

    onSave?.(sanitized, message);
  }, [value, validation.valid, loading, message, onSave]);

  const handleCancel = useCallback(() => {
    // Perguntar se houve alterações não salvas
    const sanitized = sanitizeMessageForEdit(value);
    const original = sanitizeMessageForEdit(message?.message || '');

    if (sanitized !== original && !loading) {
      const hasChanges = value.trim().length > 0 && sanitized !== original;
      // Passa flag para o callback decidir se mostra confirmação
      onCancel?.(hasChanges);
    } else {
      onCancel?.(false);
    }
  }, [value, message, loading, onCancel]);

  const handleKeyDown = useCallback(
    (e) => {
      // Ctrl+Enter ou Cmd+Enter → Salvar
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
      // Escape → Cancelar
      if (e.key === 'Escape' && !e.shiftKey) {
        e.preventDefault();
        handleCancel();
      }
      // Ctrl+Z → Desfazer
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const prevValue = undo.undo();
        if (prevValue !== null) {
          setValue(prevValue);
        }
      }
      // Ctrl+Shift+Z ou Ctrl+Y → Refazer
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        e.preventDefault();
        const nextValue = undo.redo();
        if (nextValue !== null) {
          setValue(nextValue);
        }
      }
    },
    [handleSave, handleCancel, undo]
  );

  // ==========================================================================
  // 🎨 CLASSES COMPUTADAS
  // ==========================================================================
  const charPercentage = (value.length / CONSTANTS.MAX_LENGTH) * 100;
  const charColor =
    charPercentage > 95
      ? 'text-red-500'
      : charPercentage > 80
        ? 'text-amber-500'
        : 'text-gray-400';

  const borderColor = focused
    ? validation.valid
      ? 'border-emerald-400 ring-2 ring-emerald-500/20'
      : 'border-red-400 ring-2 ring-red-500/20'
    : 'border-gray-200';

  // ==========================================================================
  // ⏸️ EARLY RETURN
  // ==========================================================================
  if (!message) return null;

  // ==========================================================================
  // 🖼️ RENDER
  // ==========================================================================
  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className={`
        relative
        bg-white rounded-2xl
        border ${borderColor}
        shadow-lg
        overflow-hidden
        transition-all duration-200
      `}
      style={{
        contain: 'layout style paint',
        willChange: 'transform, opacity, border-color',
      }}
    >
      {/* ================================================================ */}
      {/* HEADER DO EDITOR */}
      {/* ================================================================ */}
      <div
        className={`
          flex items-center justify-between
          px-3 py-2
          bg-gradient-to-r from-gray-50 to-white
          border-b border-gray-100
        `}
      >
        {/* Lado esquerdo: Indicador de edição */}
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: primaryColor }}
          />
          <span className="text-xs font-semibold text-gray-700">
            Editando mensagem
          </span>
          {undo.canUndo && (
            <button
              onClick={() => {
                const prev = undo.undo();
                if (prev !== null) setValue(prev);
              }}
              className="ml-1 p-1 hover:bg-gray-200 rounded-lg transition-colors"
              title="Desfazer (Ctrl+Z)"
              aria-label="Desfazer"
            >
              <RotateCcw size={12} className="text-gray-500" />
            </button>
          )}
        </div>

        {/* Lado direito: Expandir/Recolher */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
          title={isExpanded ? 'Recolher' : 'Expandir'}
          aria-label={isExpanded ? 'Recolher editor' : 'Expandir editor'}
        >
          {isExpanded ? (
            <Minimize2 size={14} className="text-gray-400" />
          ) : (
            <Maximize2 size={14} className="text-gray-400" />
          )}
        </button>
      </div>

      {/* ================================================================ */}
      {/* ÁREA DE TEXTO */}
      {/* ================================================================ */}
      <div className="px-3 pt-2 pb-1">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Digite sua mensagem..."
          disabled={loading}
          rows={isMobile ? 3 : 4}
          className={`
            w-full resize-none
            bg-transparent
            text-sm text-gray-800
            placeholder:text-gray-400
            leading-relaxed
            outline-none
            disabled:opacity-50 disabled:cursor-not-allowed
            scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent
          `}
          style={{
            fontFamily: "system-ui, -apple-system, sans-serif",
            lineHeight: '1.6',
            contain: 'layout style',
          }}
          aria-label="Editar mensagem"
          aria-invalid={!validation.valid}
          aria-describedby={
            !validation.valid ? 'editor-validation-message' : undefined
          }
        />
      </div>

      {/* ================================================================ */}
      {/* FOOTER: Contador + Validação + Botões */}
      {/* ================================================================ */}
      <div
        className={`
          flex items-center justify-between gap-2
          px-3 py-2
          bg-gray-50/80
          border-t border-gray-100
          ${isMobile ? 'flex-wrap' : ''}
        `}
      >
        {/* Contador de caracteres + Validação */}
        <div
          className={`
            flex items-center gap-2
            ${isMobile ? 'w-full order-2 justify-between' : 'flex-1'}
          `}
        >
          {/* Contador */}
          <span
            className={`
              text-xs font-mono font-medium
              ${charColor}
              transition-colors duration-200
            `}
          >
            {value.length}/{CONSTANTS.MAX_LENGTH}
          </span>

          {/* Barra de progresso sutil */}
          <div className="hidden sm:block w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              animate={{ width: `${Math.min(charPercentage, 100)}%` }}
              transition={{ duration: 0.2 }}
              className="h-full rounded-full"
              style={{
                backgroundColor:
                  charPercentage > 95
                    ? '#ef4444'
                    : charPercentage > 80
                      ? '#f59e0b'
                      : primaryColor,
              }}
            />
          </div>

          {/* Mensagem de validação */}
          {!validation.valid && (
            <span
              id="editor-validation-message"
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <AlertCircle size={10} />
              {validation.message}
            </span>
          )}

          {/* Indicador de alterações não salvas */}
          {validation.valid &&
            value !== sanitizeMessageForEdit(message?.message || '') && (
              <span className="hidden sm:inline text-xs text-amber-500">
                ● Alterado
              </span>
            )}
        </div>

        {/* Botões de ação */}
        <div
          className={`
            flex items-center gap-2
            ${isMobile ? 'w-full order-1 justify-end' : 'flex-shrink-0'}
          `}
        >
          {/* Cancelar */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            onClick={handleCancel}
            disabled={loading}
            className={`
              flex items-center gap-1.5
              px-3 py-2
              rounded-xl
              border border-gray-200
              text-sm font-medium text-gray-600
              hover:bg-gray-100
              active:bg-gray-200
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-all
            `}
            aria-label="Cancelar edição"
          >
            <X size={15} />
            <span className="hidden sm:inline">Cancelar</span>
          </motion.button>

          {/* Salvar */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            onClick={handleSave}
            disabled={!validation.valid || loading}
            className={`
              flex items-center gap-1.5
              px-4 py-2
              rounded-xl
              text-sm font-semibold text-white
              transition-all
              disabled:opacity-50 disabled:cursor-not-allowed
              shadow-sm
            `}
            style={{
              backgroundColor: validation.valid ? primaryColor : '#9ca3af',
              contain: 'layout style paint',
            }}
            aria-label="Salvar alterações"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                <span className="hidden sm:inline">Salvando...</span>
              </>
            ) : (
              <>
                <Check size={15} />
                <span className="hidden sm:inline">Salvar</span>
              </>
            )}
          </motion.button>
        </div>
      </div>

      {/* ================================================================ */}
      {/* DICA DE ATALHOS (apenas desktop, aparece no hover) */}
      {/* ================================================================ */}
      {!isMobile && focused && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute -bottom-8 left-3 flex items-center gap-3 text-[10px] text-gray-400 pointer-events-none"
        >
          <span>
            <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">
              Ctrl+Enter
            </kbd>{' '}
            Salvar
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">
              Esc
            </kbd>{' '}
            Cancelar
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">
              Ctrl+Z
            </kbd>{' '}
            Desfazer
          </span>
        </motion.div>
      )}
    </motion.div>
  );
}

// ============================================================================
// 🎯 MEMO + DISPLAY NAME
// ============================================================================
InlineMessageEditor.displayName = 'InlineMessageEditor';

// ============================================================================
// 🎯 EXPORTAÇÃO
// ============================================================================
export default memo(InlineMessageEditor);